import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, Descriptions, Typography, Breadcrumb, Flex, Divider, Grid, message, theme, Tag } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { ConnectorProduct } from '../types/product';
import { fetchProductDetail } from '../api/products';
import InquiryModal from './common/InquiryModal';
import { useTranslation } from 'react-i18next';
import { isConnectorProduct } from '../types/product'; // 导入类型守卫

// 导入所有连接器图片
import ProductImg_connectorZH05 from '@/assets/images/product/connector_ZH05Series.jpg';
import ProductImg_connectorZG05 from '@/assets/images/product/connector_ZG05Series.jpg';
import ProductImg_connectorBK13 from '@/assets/images/product/connector_BK13Series.jpg';
import ProductImg_connectorBM20 from '@/assets/images/product/connector_BM20Series.jpg';
import ProductImg_connectorBM23PF from '@/assets/images/product/connector_BM23PFSeries.jpg';
import ProductImg_connectorBM28 from '@/assets/images/product/connector_BM28Series.jpg';
import ProductImg_connectorBM29 from '@/assets/images/product/connector_BM29Series.jpg';
import ProductImg_connectorBM54F from '@/assets/images/product/connector_BM54FSeries.jpg';
import ProductImg_connectorDF12N from '@/assets/images/product/connector_DF12NSeries.jpg';
import ProductImg_connectorDF23 from '@/assets/images/product/connector_DF23Series.jpg';
import ProductImg_connectorDF40 from '@/assets/images/product/connector_DF40Series.jpg';
import ProductImg_connectorDF40T from '@/assets/images/product/connector_DF40TSeries.jpg';
import ProductImg_connectorDF58 from '@/assets/images/product/connetor_DF58Series.jpg';
import ProductImg_connectorEM30MDS from '@/assets/images/product/connector_EM30MSDSeries.jpg';
import ProductImg_connectorFH28 from '@/assets/images/product/connector_FH28Series.jpg';
import ProductImg_connectorFH28K from '@/assets/images/product/connector_FH28KSeries.jpg';
import ProductImg_connectorFH34 from '@/assets/images/product/connector_FH34Series.jpg';
import ProductImg_connectorFH36 from '@/assets/images/product/connector_FH36Series.jpg';
import ProductImg_connectorFH41 from '@/assets/images/product/connector_FH41Series.jpg';
import ProductImg_connectorFH52 from '@/assets/images/product/connector_FH52Series.jpg';
import ProductImg_connectorFH63S from '@/assets/images/product/connector_FH63SSeries.jpg';
import ProductImg_connectorFH64MA from '@/assets/images/product/connector_FH64MASeries.jpg';
import ProductImg_connectorFH65 from '@/assets/images/product/connector_FH65Series.jpg';
import ProductImg_connectorFH67 from '@/assets/images/product/connector_FH67Series.jpg';
import ProductImg_connectorFH82 from '@/assets/images/product/connector_FH82Series.jpg';
import ProductImg_connectorFX18 from '@/assets/images/product/connector_FX18Series.jpg';
import ProductImg_connectorFX23 from '@/assets/images/product/connector_FX23Series.jpg';
import ProductImg_connectorTF31 from '@/assets/images/product/connector_TF31Series.jpg';
import ProductImg_connectorTF38 from '@/assets/images/product/connector_TF38Series.jpg';
import ProductImg_connectorUFL from '@/assets/images/product/connector_UFLSeries.jpg';
import ProductImg_connectorZE05 from '@/assets/images/product/connector_ZE05Series.jpg';

const { useToken } = theme;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const keyToChineseMap: { [key: string]: string } = {
  connector_type: 'Connector Type',
  contact_pitch: 'Contact Pitch',
  mated_height_max: 'Mated Height',
  rated_current: 'Rated Current',
  rated_current_other: 'Rated Current Other',
  mounting_style: 'Mounting Style',
  positions_No: 'Number of Positions',
  COO: 'COO',
  spq: 'SPQ',
  brand: 'Brand',
};

const ProductDetailConnector: FC = () => {
  const { t } = useTranslation();
  const { token } = useToken();
  const rawPartNumber = useParams().part_number || '';
  const part_number = decodeURIComponent(rawPartNumber);
  const { type } = useParams<{ type: string }>();
  const [product, setProduct] = useState<ConnectorProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const screens = useBreakpoint();

  useEffect(() => {
    const loadData = async () => {
      try {
        //console.log('正在请求:', { part_number, type }); 
        if (!part_number || !type) return;
        const data = await fetchProductDetail(part_number, type);
        //console.log('收到响应:', data); 
        if (data && isConnectorProduct(data)) {
          setProduct(data);
        } else {
          setError('产品类型不匹配或未找到产品');
          message.error('产品类型不匹配或未找到产品');
        }
      } catch (error) {
        console.error('加载产品详情失败:', error);
        setError('加载产品详情失败，请稍后再试');
        message.error('加载产品详情失败，请稍后再试');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [part_number, type]);

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
  }

  if (error) {
    return <div style={{ padding: 20 }}>{error}</div>;
  }

  if (!product) {
    return <div style={{ padding: 20 }}>产品未找到</div>;
  }

  // 根据产品类型获取对应的路由路径
  const getProductTypePath = (type: string) => {
    switch (type) {
      case 'ConnectorBTB':
        return '/products/connector/ConnectorBTB';
      case 'ConnectorZIF':
        return '/products/connector/ConnectorZIF';
      case 'ConnectorWTB':
        return '/products/connector/ConnectorWTB';
      case 'ServicePlug':
        return '/products/connector/ServicePlug';
      case 'ConnectorMC':
        return '/products/connector/ConnectorMC';
      case 'ConnectorAuto':
        return '/products/connector/ConnectorAuto';
      default:
        return '/products';
    }
  };

  // 获取产品图片
  const getProductImage = (partNumber: string) => {
    if (partNumber.startsWith('ZH05')) return ProductImg_connectorZH05;
    if (partNumber.startsWith('ZG05')) return ProductImg_connectorZG05;
    if (partNumber.startsWith('BK13')) return ProductImg_connectorBK13;
    if (partNumber.startsWith('BM20')) return ProductImg_connectorBM20;
    if (partNumber.startsWith('BM23PF')) return ProductImg_connectorBM23PF;
    if (partNumber.startsWith('BM28')) return ProductImg_connectorBM28;
    if (partNumber.startsWith('BM29')) return ProductImg_connectorBM29;
    if (partNumber.startsWith('BM54F')) return ProductImg_connectorBM54F;
    if (partNumber.startsWith('DF12N')) return ProductImg_connectorDF12N;
    if (partNumber.startsWith('DF23')) return ProductImg_connectorDF23;
    if (partNumber.startsWith('DF40T')) return ProductImg_connectorDF40T;
    if (partNumber.startsWith('DF40')) return ProductImg_connectorDF40;
    if (partNumber.startsWith('DF58')) return ProductImg_connectorDF58;
    if (partNumber.startsWith('EM30MDS')) return ProductImg_connectorEM30MDS;
    if (partNumber.startsWith('FH28K')) return ProductImg_connectorFH28K;
    if (partNumber.startsWith('FH28')) return ProductImg_connectorFH28;
    if (partNumber.startsWith('FH34')) return ProductImg_connectorFH34;
    if (partNumber.startsWith('FH36')) return ProductImg_connectorFH36;
    if (partNumber.startsWith('FH41')) return ProductImg_connectorFH41;
    if (partNumber.startsWith('FH52')) return ProductImg_connectorFH52;
    if (partNumber.startsWith('FH63S')) return ProductImg_connectorFH63S;
    if (partNumber.startsWith('FH64MA')) return ProductImg_connectorFH64MA;
    if (partNumber.startsWith('FH65')) return ProductImg_connectorFH65;
    if (partNumber.startsWith('FH67')) return ProductImg_connectorFH67;
    if (partNumber.startsWith('FH82')) return ProductImg_connectorFH82;
    if (partNumber.startsWith('FX18')) return ProductImg_connectorFX18;
    if (partNumber.startsWith('FX23')) return ProductImg_connectorFX23;
    if (partNumber.startsWith('TF31')) return ProductImg_connectorTF31;
    if (partNumber.startsWith('TF38')) return ProductImg_connectorTF38;
    if (partNumber.startsWith('UFL')) return ProductImg_connectorUFL;
    if (partNumber.startsWith('ZE05')) return ProductImg_connectorZE05;
    
    // 默认返回 ZH05 图片
    return ProductImg_connectorZH05;
  };

  return (
    <div style={{ paddingBottom: '80px' }}>
      <div style={{ background: '#f5f5f5' }}>
        <div className="container">
          <Breadcrumb
            className="breadcrumbs"
            items={[
              {
                title: (
                  <>
                    <HomeOutlined />
                    <span>{t('home')}</span>
                  </>
                ),
                href: '/',
              },
              {
                title: t('page.product.title'),
                href: '/products',
              },
              {
                title: t(`page.product.categories.connector.${product.product_type.toLowerCase()}`),
                href: getProductTypePath(product.product_type),
              },
              {
                title: product.part_number,
              },
            ]}
          />
        </div>
      </div>

      <div className="container" style={{ marginTop: 30 }}>
        <Flex gap={screens.md ? 48 : 30} align="stretch" wrap="wrap">
          {/* 产品图片区 */}
          <div className='text-center' style={{
            flex: screens.md ? '' : '0 0 100%',
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            padding: 16,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <img 
              src={getProductImage(product.part_number)} 
              alt={product.get_product_type_display}
            />
          </div>
          {/* 产品基础信息区 */}
          <Flex vertical gap={16} style={{ flex: '1' }}>
            <Title level={1} className='mb-0 text-primary' style={{fontSize: token.fontSizeHeading2}}>
              {product.part_number}
            </Title>

            <Divider style={{ margin: '0' }} />

            <Flex vertical gap={12}>
              <Flex gap={8} align="center">
                <Text style={{ minWidth: 80 }}>产品类型:</Text>
                <Text>{product.get_product_type_display}</Text>
              </Flex>
              <Flex gap={8} align="center">
                <Text style={{ minWidth: 80 }}>规格型号:</Text>
                <Text>{product.specification || '-'}</Text>
              </Flex>

              {/* 立即询价 */}
              <InquiryModal part_number={product.part_number} />
            </Flex>
          </Flex>
          {/* 库存信息区 */}
          <div className='' style={{
            flex: screens.md ? '0 0 420px' : '0 0 100%',
            background: '#f9fafe',
            borderRadius: 8,
            padding: 24,
            border: '1px solid #e8eef7'
          }}>
            <Title level={4} style={{ 
              color: '#1a3353',
              marginBottom: 16,
              fontWeight: 500
            }}>
              库存信息
            </Title>
            <Flex vertical gap={12}>
              <Flex justify="space-between">
                <Text type="secondary">现货库存</Text>
                <Tag color="success" style={{marginInlineEnd: 0}}>库存充足</Tag>
              </Flex>
              <Flex justify="space-between">
                <Text type="secondary">交期</Text>
                <Text strong>3-5工作日</Text>
              </Flex>
              <Flex justify="space-between">
                <Text type="secondary">最小起订量</Text>
	<Text strong>{product.specs.spq}</Text>
              </Flex>
            </Flex>
          </div>
        </Flex>

        {/* 产品规格参数 */}
        <div style={{
          marginTop: 30,
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: 24
        }}>
          <Title level={2} className='mb-20'>产品规格参数</Title>
          <Descriptions bordered column={screens.md ? 2 : 1}>
            {Object.entries(product.specs).filter(([key]) => key !== 'id' && key !== 'product').map(([key, value]) => (
              <Descriptions.Item key={key} label={keyToChineseMap[key] || key}>
                {value || '-'}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailConnector; 